import type { BoardData, ConnectorType } from '../../model/schema.js';
import { connectionEntities, defineRule, resolvedConnections } from '../rule.js';

/** Connector pairs that mate without an adapter (besides identical ones). */
const COMPATIBLE: ReadonlyArray<readonly [ConnectorType, ConnectorType]> = [
  ['ethercon', 'rj45'],
  ['trs', 'ts'],
];

/** Cable types that announce an adapter, e.g. "TRS->XLR", "XLR→Jack", "adapter cable". */
export const ADAPTER_NOTATION = /adapter|adaptor|->|→/i;

/** True when two connectors mate directly. */
export function connectorsCompatible(a: ConnectorType, b: ConnectorType): boolean {
  return a === b || COMPATIBLE.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
}

/** Items of kind `generic` with `props.adapter === true` are adapters. */
export function isAdapterItem(data: BoardData, itemId: string): boolean {
  const item = data.items[itemId];
  return item?.kind === 'generic' && item.props['adapter'] === true;
}

/**
 * W: the two connectors of a cable do not mate. Only checked when both ports declare a connector;
 * skipped when the cable type denotes an adapter or one end is an adapter item.
 */
export const connectorMismatch = defineRule(
  'connector-mismatch',
  'warning',
  { en: 'Connector mismatch', de: 'Stecker passen nicht' },
  (data, emit) =>
    resolvedConnections(data).flatMap(({ connection: c, from, to }) => {
      if (from.connector === undefined || to.connector === undefined) return [];
      if (connectorsCompatible(from.connector, to.connector)) return [];
      if (ADAPTER_NOTATION.test(c.cableType ?? '')) return [];
      if (isAdapterItem(data, c.from.itemId) || isAdapterItem(data, c.to.itemId)) return [];
      return [emit(connectionEntities(c), { from: from.connector, to: to.connector })];
    }),
);
